import { useParams } from 'react-router-dom';
import { config } from "../../data/config";
import { useEffect } from 'react';
import { getCookies } from "cookies-next";
import axios from "axios";
import { useState } from "react";
import BreadCrump from "../tools/breadcrump"
import { useTranslation } from 'react-i18next';
import HeaderOfPage from '../tools/headerOfPage';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import CountrySelector from './CountrySelector';
import ImageUploaders from '../tools/uploaderimages';

type LanguageCode = string;

function resolveParentId(parent: unknown): string {
    if (!parent) return '';
    if (typeof parent === 'string') return parent;
    if (typeof parent === 'object' && parent !== null && '_id' in parent) {
        const id = (parent as { _id?: unknown })._id;
        return id != null ? String(id) : '';
    }
    return String(parent);
}

function buildTestimonialSavePayload(source: Record<string, unknown>) {
    return {
        parent: resolveParentId(source.parent),
        title: source.title,
        description: source.description,
        country: typeof source.country === 'string' ? source.country : '',
        url: typeof source.url === 'string' ? source.url.trim() : '',
        date: source.date,
        tags: source.tags,
        publish: source.publish,
    };
}

export default function EditTestimonialDetail() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    let cookie = getCookies();
    let { Id } = useParams();
    const [key, setKey] = useState<any>(1);
    const [error, setError] = useState<any>(null);
    const [activeLang, setActiveLang] = useState<LanguageCode>('en');
    const [tagInput, setTagInput] = useState<string>('');
    const [languages, setLanguages] = useState<Array<{ code: LanguageCode; label: string }>>([]);

    const [data, setData] = useState<any>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [translating, setTranslating] = useState<boolean>(false);
    const [groups, setGroups] = useState<any[]>([]);

    // Translate text: try backend first, then Google fallback
    const translateText = async (text: string, fromLang: LanguageCode, toLang: LanguageCode): Promise<string> => {
        if (!text || String(text).trim() === '') return '';
        if (fromLang === toLang) return text;
        try {
            try {
                const response = await axios.post(
                    `${config.url}/v1/translate`,
                    { text: text.trim(), from: fromLang, to: toLang, format: 'text' },
                    { headers: { Authorization: 'Bearer ' + cookie['token'] } }
                );
                if (response.data?.translatedText) return response.data.translatedText;
            } catch (_) {
                // Backend not available, use Google
            }
            const response = await fetch(
                `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${fromLang}&tl=${toLang}&dt=t&q=${encodeURIComponent(text.trim())}`
            );
            if (!response.ok) return '';
            const result = await response.json();
            const translated = result[0]?.map((s: any) => s?.[0] || '').join('').trim();
            return translated || text;
        } catch (_) {
            return '';
        }
    };

    const handleAutoTranslate = async () => {
        const titleText = (data.title?.[activeLang] || '').trim();
        const descText = (data.description?.[activeLang] || '').trim();
        if (!titleText && !descText) {
            Swal.fire({
                title: t('No content to translate'),
                text: t('Please add content in the current language first'),
                icon: 'warning',
            });
            return;
        }
        const confirmed = await Swal.fire({
            title: t('Auto translate'),
            text: t('This will translate the current content to all other languages. Continue?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('Yes, translate'),
            cancelButtonText: t('Cancel'),
        });
        if (!confirmed.isConfirmed) return;

        setTranslating(true);
        try {
            const targetLangs = languages.map((l) => l.code).filter((code) => code !== activeLang);
            const newTitle = { ...data.title };
            const newDescription = { ...data.description };

            for (const toLang of targetLangs) {
                if (titleText) {
                    const translated = await translateText(titleText, activeLang, toLang);
                    if (translated) newTitle[toLang] = translated;
                    await new Promise((r) => setTimeout(r, 120));
                }
                if (descText) {
                    const translated = await translateText(descText, activeLang, toLang);
                    if (translated) newDescription[toLang] = translated;
                    await new Promise((r) => setTimeout(r, 120));
                }
            }
            setData({ ...data, title: newTitle, description: newDescription });
            Swal.fire({
                title: t('Translation completed'),
                text: t('Content has been translated to all other languages'),
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (err: any) {
            Swal.fire({
                title: t('Translation failed'),
                text: err?.message || t('An error occurred during translation'),
                icon: 'error',
            });
        } finally {
            setTranslating(false);
        }
    };
    
    const save = async () => {
        setSaving(true);
        try {
            const path = `${config.url}/v1/dashboard/testimonialdetail/${Id}`;
            const payload = buildTestimonialSavePayload(data);

            const res = await axios({
                method: 'put',
                data: payload,
                url: path,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                    'Content-Type': 'application/json',
                }
            });

            const saved = res.data?.data ?? res.data?.message;
            if (saved && typeof saved === 'object') {
                setData((prev: Record<string, unknown> | null) => ({
                    ...prev,
                    ...saved,
                    url: typeof saved.url === 'string' ? saved.url : payload.url,
                    parent: saved.parent ?? payload.parent,
                }));
            } else {
                setData((prev: Record<string, unknown> | null) =>
                    prev ? { ...prev, url: payload.url } : prev
                );
            }

            Swal.fire({
                icon: 'success',
                title: t('Success'),
                text: t('Testimonial updated successfully'),
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Error updating testimonial'),
            });
        } finally {
            setSaving(false);
        }
    }
    
    // Load languages
    const loadLanguages = async (): Promise<Array<{ code: LanguageCode; label: string }>> => {
        try {
            const res = await axios.get(`${config.url}/v1/language`);
            const languageList = res.data.message || [];
            const mappedLanguages = languageList
                .filter((lang: any) => lang.active)
                .sort((a: any, b: any) => (a.arranged || 0) - (b.arranged || 0))
                .map((lang: any) => ({
                    code: lang.code as LanguageCode,
                    label: lang.label
                }));
            setLanguages(mappedLanguages);
            if (mappedLanguages.length > 0) {
                setActiveLang(mappedLanguages[0].code);
            }
            return mappedLanguages;
        } catch (error) {
            console.error('Error loading languages:', error);
            const fallbackLanguages = [
                { code: 'ku' as LanguageCode, label: 'Kurdish' },
                { code: 'fa' as LanguageCode, label: 'فارسی' },
                { code: 'en' as LanguageCode, label: 'English' },
                { code: 'ar' as LanguageCode, label: 'العربية' },
                { code: 'tr' as LanguageCode, label: 'Türkçe' }
            ];
            setLanguages(fallbackLanguages);
            return fallbackLanguages;
        }
    };

    // Helper function to initialize multilingual field
    const initializeMultilingualField = (field: any, languageCodes: string[]): any => {
        if (!field) {
            const obj: any = {};
            languageCodes.forEach(code => obj[code] = '');
            return obj;
        }
        if (typeof field === 'string') {
            const obj: any = {};
            languageCodes.forEach(code => obj[code] = field);
            return obj;
        }
        if (typeof field === 'object') {
            if (field.nl) {
                field.ku = field.nl;
                delete field.nl;
            }
            const obj: any = { ...field };
            languageCodes.forEach(code => {
                if (!obj[code]) obj[code] = '';
            });
            return obj;
        }
        const obj: any = {};
        languageCodes.forEach(code => obj[code] = '');
        return obj;
    };

    const load = async (languageCodes: string[] = []) => {
        try {
            var path = `${config.url}/v1/dashboard/testimonialdetail/${Id}`;

            let res = await axios({
                method: 'get',
                url: path,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });
            const loadedData = res.data.message;
            const codes = languageCodes.length > 0 
                ? languageCodes
                : ['ku', 'fa', 'en', 'ar', 'tr'];
            
            // Ensure multilingual structure exists
            if (loadedData) {
                loadedData.title = initializeMultilingualField(loadedData.title, codes);
                loadedData.description = initializeMultilingualField(loadedData.description, codes);
                // Ensure tags structure
                if (!loadedData.tags || typeof loadedData.tags !== 'object') {
                    loadedData.tags = {};
                    codes.forEach(code => loadedData.tags[code] = []);
                } else {
                    codes.forEach(code => {
                        if (!Array.isArray(loadedData.tags[code])) {
                            loadedData.tags[code] = [];
                        }
                    });
                }
                // Format date
                if (loadedData.date) {
                    const date = new Date(loadedData.date);
                    loadedData.date = date.toISOString().split('T')[0];
                } else {
                    loadedData.date = new Date().toISOString().split('T')[0];
                }
                if (loadedData.url == null) {
                    loadedData.url = '';
                }
            }
            setData(loadedData)
        } catch (error) {
            setError(error);
        }
    }

    const loadGroups = async () => {
        try {
            let res = await axios({
                method: 'get',
                url: config.url + '/v1/dashboard/testimonialgroup/all',
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });
            setGroups(res.data.message || []);
        } catch (error) {
            console.error('Error loading groups:', error);
        }
    }
    
    const inputstyle = "py-3 px-5 block w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:pointer-events-none bg-white text-gray-900";
    
    const breadcrump = [
        {
            name: 'Dashboard',
            url: '/dashboard'
        },
        {
            name: 'Testimonial',
            url: '/dashboard/Feature'
        },
        {
            name: 'Group List',
            url: '/dashboard/Testimonial/GroupList'
        },
        {
            name: 'Edit Testimonial',
            url: null
        }
    ]

    const getDisplayName = (name: any): string => {
        if (!name) return '';
        if (typeof name === 'string') return name;
        if (typeof name === 'object') {
            return name.en || name.fa || name.ku || name.ar || name.tr || '';
        }
        return String(name);
    };

    const addTag = () => {
        if (tagInput.trim() && !data.tags[activeLang].includes(tagInput.trim())) {
            setData({
                ...data,
                tags: {
                    ...data.tags,
                    [activeLang]: [...data.tags[activeLang], tagInput.trim()]
                }
            });
            setTagInput('');
        }
    };

    const removeTag = (tag: string) => {
        setData({
            ...data,
            tags: {
                ...data.tags,
                [activeLang]: data.tags[activeLang].filter((t: string) => t !== tag)
            }
        });
    };
    
    useEffect(() => {
        const initialize = async () => {
            const loadedLanguages = await loadLanguages();
            const languageCodes = loadedLanguages.length > 0
                ? loadedLanguages.map(l => l.code)
                : ['ku', 'fa', 'en', 'ar', 'tr'];
            await Promise.all([load(languageCodes), loadGroups()]);
        };
        initialize();
    }, []);

    // Update data when languages change
    useEffect(() => {
        if (languages.length > 0 && data) {
            const languageCodes = languages.map(l => l.code);
            const updatedData = { ...data };
            updatedData.title = initializeMultilingualField(data.title, languageCodes);
            updatedData.description = initializeMultilingualField(data.description, languageCodes);
            updatedData.url = typeof data.url === 'string' ? data.url : '';
            if (!updatedData.tags) updatedData.tags = {};
            languageCodes.forEach(code => {
                if (!Array.isArray(updatedData.tags[code])) {
                    updatedData.tags[code] = [];
                }
            });
            setData(updatedData);
        }
    }, [languages]);
    
    if (!data) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 md:p-6">
                <BreadCrump data={breadcrump}></BreadCrump>
                <HeaderOfPage name="Edit Testimonial"></HeaderOfPage>
                <div className="bg-white rounded-xl shadow-sm p-8 mt-6">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                    <p className="text-center mt-4 text-gray-600">در حال بارگذاری...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-6">
            <BreadCrump data={breadcrump}></BreadCrump>
            <HeaderOfPage name="Edit Testimonial"></HeaderOfPage>

            {data != null && (
                <div className="mt-6">
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden" dir='rtl'>
                        <div className="p-6 space-y-6">
                            <form key={key} className='flex flex-col gap-4'>
                                {/* Language Selector + Auto Translate */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('Language')}
                                    </label>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {languages.map((lang) => (
                                            <button
                                                key={lang.code}
                                                type="button"
                                                onClick={() => setActiveLang(lang.code)}
                                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                                    activeLang === lang.code
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                            >
                                                {lang.label}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={handleAutoTranslate}
                                            disabled={translating}
                                            title={t('Auto translate to all other languages')}
                                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-green-600 text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {translating ? (
                                                <>
                                                    <span className="animate-spin inline-block w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full" />
                                                    <span>{t('Translating...')}</span>
                                                </>
                                            ) : (
                                                <span>{t('Auto translate to all other languages')}</span>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('Parent Group')}
                                    </label>
                                    <select
                                        value={data.parent?._id || data.parent || ''}
                                        onChange={(e) => setData({ ...data, parent: e.target.value })}
                                        className={inputstyle}
                                    >
                                        <option value="">{t('Select Group')}</option>
                                        {groups.map((group: any) => (
                                            <option key={group._id || group.id} value={group._id || group.id}>
                                                {getDisplayName(group.title)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('Title')} ({languages.find(l => l.code === activeLang)?.label}) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        value={data.title?.[activeLang] || ''}
                                        type="text"
                                        className={`${inputstyle} ${error != null && error['title'] != undefined && 'bg-red-100 border-red-300'} `}
                                        placeholder={t('Enter title')}
                                        onChange={(e) => {
                                            setData({ 
                                                ...data, 
                                                title: { 
                                                    ...data.title, 
                                                    [activeLang]: e.target.value 
                                                }
                                            });
                                        }}
                                    />
                                    {error != null && error['title'] != undefined && <small className="text-red-500 text-xs mt-1 block">{error['title']['message']}</small>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('Description')} ({languages.find(l => l.code === activeLang)?.label})
                                    </label>
                                    <textarea
                                        value={data.description?.[activeLang] || ''}
                                        rows={4}
                                        className={inputstyle}
                                        placeholder={t('Enter description')}
                                        onChange={(e) => {
                                            setData({ 
                                                ...data, 
                                                description: { 
                                                    ...data.description, 
                                                    [activeLang]: e.target.value 
                                                }
                                            });
                                        }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('Country')}
                                    </label>
                                    <CountrySelector
                                        value={data.country || ''}
                                        onChange={(value) => setData({ ...data, country: value })}
                                        placeholder={t('Select country')}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('Date')}
                                    </label>
                                    <input
                                        type="date"
                                        value={data.date || ''}
                                        className={inputstyle}
                                        onChange={(e) => setData({ ...data, date: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('URL address')}
                                    </label>
                                    <input
                                        type="url"
                                        value={data.url || ''}
                                        className={inputstyle}
                                        placeholder="https://example.com"
                                        onChange={(e) => setData({ ...data, url: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('Tags')} ({languages.find(l => l.code === activeLang)?.label})
                                    </label>
                                    <div className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={tagInput}
                                            className={inputstyle}
                                            placeholder={t('Enter tag and press Enter')}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addTag();
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={addTag}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                        >
                                            {t('Add')}
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {data.tags && data.tags[activeLang] && data.tags[activeLang].map((tag: string, index: number) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                                            >
                                                {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => removeTag(tag)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={data.publish !== false}
                                            onChange={(e) => setData({ ...data, publish: e.target.checked })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">{t('Publish')}</span>
                                    </label>
                                </div>

                                <div className="flex gap-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={save}
                                        disabled={saving}
                                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {saving ? t('Saving...') : t('Save')}
                                    </button>
                                </div>
                            </form>
                            <ImageUploaders count={1000} id={data.id} component={'testimonialdetail'}></ImageUploaders>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

